"use client";
import Marquee from "react-fast-marquee";
import { useEffect, useState } from "react";
import { urlFor } from "@/sanity/lib/image";
import { cn } from "@/lib/utils";

interface MarqueeItem {
  _id?: string;
  name: string;
  logo?: {
    asset: any;
    alt?: string;
  };
  url?: string;
}

type SanityLogo = {
  asset: unknown;
  alt?: string;
};

type SanityClientItem = { _id: string; name: string; logo?: SanityLogo };
type SanityToolItem = { _id: string; title: string; icon?: SanityLogo; hasLogo?: boolean };
type PublicContentType = "allClients" | "allTools";

interface MarqueeShowcaseProps {
  heading?: string;
  subheading?: string;
  description?: string;
  type?: "allClients" | "allTools" | "custom";
  customClients?: Array<{
    _id: string;
    name: string;
    logo?: {
      asset: any;
      alt?: string;
    };
  }>;
  customTools?: Array<{
    _id: string;
    title: string;
    icon?: {
      asset: any;
      alt?: string;
    };
  }>;
  customItems?: MarqueeItem[];
  prefetchedItems?: MarqueeItem[];
  prefetchedSecondRowItems?: MarqueeItem[];
  styling?: {
    backgroundColor?: string;
    padding?: string;
    textAlignment?: string;
    logoStyle?: string;
    logoOpacity?: number;
    speed?: number;
    showSecondRow?: boolean;
    secondRowType?: "same" | "custom" | "allClients" | "allTools";
    secondRowCustomClients?: Array<{
      _id: string;
      name: string;
      logo?: {
        asset: any;
        alt?: string;
      };
    }>;
    secondRowCustomTools?: Array<{
      _id: string;
      title: string;
      icon?: {
        asset: any;
        alt?: string;
      };
    }>;
    secondRowCustomItems?: MarqueeItem[];
    secondRowSpeed?: number;
  };
}

const EMPTY_CUSTOM_CLIENTS: NonNullable<MarqueeShowcaseProps["customClients"]> = [];
const EMPTY_CUSTOM_TOOLS: NonNullable<MarqueeShowcaseProps["customTools"]> = [];
const EMPTY_CUSTOM_ITEMS: NonNullable<MarqueeShowcaseProps["customItems"]> = [];
const EMPTY_STYLING: NonNullable<MarqueeShowcaseProps["styling"]> = {};
const publicContentCache: Partial<Record<PublicContentType, unknown[]>> = {};
const publicContentInFlight: Partial<Record<PublicContentType, Promise<unknown[]>>> = {};

const mapClientsToMarqueeItems = (clients: SanityClientItem[]): MarqueeItem[] =>
  (clients ?? []).map((client) => ({
    _id: client._id,
    name: client.name,
    logo: client.logo,
  }));

const mapToolsToMarqueeItems = (tools: SanityToolItem[]): MarqueeItem[] =>
  (tools ?? [])
    .filter((tool) => tool.hasLogo)
    .map((tool) => ({
      _id: tool._id,
      name: tool.title,
      logo: tool.icon,
    }));

export default function MarqueeShowcase(props: MarqueeShowcaseProps) {
  // console.log("MarqueeShowcase props:", props);
  
  const {
    heading,
    subheading,
    description,
    type = "allClients",
    customClients: rawCustomClients,
    customTools: rawCustomTools,
    customItems: rawCustomItems,
    prefetchedItems,
    prefetchedSecondRowItems,
    styling: rawStyling,
  } = props;

  // Keep fallback references stable so effect deps don't retrigger on every render.
  const customClients = rawCustomClients ?? EMPTY_CUSTOM_CLIENTS;
  const customTools = rawCustomTools ?? EMPTY_CUSTOM_TOOLS;
  const customItems = rawCustomItems ?? EMPTY_CUSTOM_ITEMS;
  const styling = rawStyling ?? EMPTY_STYLING;

  const initialItems: MarqueeItem[] = prefetchedItems
    ? prefetchedItems
    : type === "custom"
      ? [
          ...(customClients ?? []).map((client) => ({
            _id: client._id,
            name: client.name,
            logo: client.logo,
          })),
          ...(customTools ?? []).map((tool) => ({
            _id: tool._id,
            name: tool.title,
            logo: tool.icon,
          })),
          ...(customItems || []),
        ]
      : type === "allClients"
        ? mapClientsToMarqueeItems(
            (publicContentCache.allClients as SanityClientItem[] | undefined) ?? [],
          )
        : mapToolsToMarqueeItems(
            (publicContentCache.allTools as SanityToolItem[] | undefined) ?? [],
          );

  const initialSecondRowItems: MarqueeItem[] = prefetchedSecondRowItems
    ? prefetchedSecondRowItems
    : styling.showSecondRow && styling.secondRowType === "allClients"
      ? mapClientsToMarqueeItems(
          (publicContentCache.allClients as SanityClientItem[] | undefined) ?? [],
        )
      : styling.showSecondRow && styling.secondRowType === "allTools"
        ? mapToolsToMarqueeItems(
            (publicContentCache.allTools as SanityToolItem[] | undefined) ?? [],
          )
        : [];
  const shouldShowInitialLoading =
    !prefetchedItems && type !== "custom" && initialItems.length === 0;

  // console.log("MarqueeShowcase destructured props:", {
  //   heading,
  //   subheading,
  //   description,
  //   type,
  //   customClients: customClients?.length || 0,
  //   customTools: customTools?.length || 0,
  //   customItems: customItems?.length || 0,
  //   styling
  // });

  const {
    backgroundColor = "bg-bg",
    padding = "py-8",
    textAlignment = "text-center",
    logoStyle = "grayscale",
    logoOpacity = 50,
    speed = 80
  } = styling;

  // Single state for all items
  const [items, setItems] = useState<MarqueeItem[]>(initialItems);
  const [secondRowItems, setSecondRowItems] = useState<MarqueeItem[]>(initialSecondRowItems);
  const [isLoading, setIsLoading] = useState(shouldShowInitialLoading);
  const [hasFetched, setHasFetched] = useState(
    !!prefetchedItems || type === "custom" || initialItems.length > 0,
  );

  const fetchPublicItems = async <T,>(contentType: PublicContentType): Promise<T[]> => {
    const cached = publicContentCache[contentType] as T[] | undefined;
    if (cached) {
      return cached;
    }

    const inflight = publicContentInFlight[contentType] as Promise<T[]> | undefined;
    if (inflight) {
      return inflight;
    }

    const request = fetch(`/api/public-content?type=${contentType}`, {
      method: "GET",
      cache: "force-cache",
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch ${contentType}`);
      }
      const data = (await response.json()) as T[];
      publicContentCache[contentType] = data as unknown[];
      return data;
    }).finally(() => {
      delete publicContentInFlight[contentType];
    });

    publicContentInFlight[contentType] = request as Promise<unknown[]>;
    return request;
  };

  // Fetch data when needed
  useEffect(() => {
    if (prefetchedItems) {
      setItems(prefetchedItems);
      setSecondRowItems(prefetchedSecondRowItems ?? []);
      setIsLoading(false);
      setHasFetched(true);
      return;
    }

    const fetchData = async () => {
      if (shouldShowInitialLoading) {
        setIsLoading(true);
      }
      try {
        // Fetch first row data
        let firstRowData: MarqueeItem[] = [];
        if (type === "custom") {
          const customClientItems = customClients?.map(client => ({
            _id: client._id,
            name: client.name,
            logo: client.logo,
          })) || [];
          
          const customToolItems = customTools?.map(tool => ({
            _id: tool._id,
            name: tool.title,
            logo: tool.icon,
          })) || [];
          
          firstRowData = [...customClientItems, ...customToolItems, ...(customItems || [])];
        } else if (type === "allClients") {
          const clients = await fetchPublicItems<SanityClientItem>("allClients");
          firstRowData = mapClientsToMarqueeItems(clients ?? []);
        } else if (type === "allTools") {
          const tools = await fetchPublicItems<SanityToolItem>("allTools");
          firstRowData = mapToolsToMarqueeItems(tools ?? []);
        }
        setItems(firstRowData);

        // Fetch second row data if enabled
        if (styling.showSecondRow) {
          let secondRowData: MarqueeItem[] = [];
          
          if (styling.secondRowType === "same") {
            // Use the same data as first row (will be staggered in rendering)
            secondRowData = firstRowData;
          } else if (styling.secondRowType === "custom") {
            const customClientItems = styling.secondRowCustomClients?.map((client: any) => ({
              _id: client._id,
              name: client.name,
              logo: client.logo,
            })) || [];
            
            const customToolItems = styling.secondRowCustomTools?.map((tool: any) => ({
              _id: tool._id,
              name: tool.title,
              logo: tool.icon,
            })) || [];
            
            secondRowData = [...customClientItems, ...customToolItems, ...(styling.secondRowCustomItems || [])];
          } else if (styling.secondRowType === "allClients") {
            const clients = await fetchPublicItems<SanityClientItem>("allClients");
            secondRowData = mapClientsToMarqueeItems(clients ?? []);
          } else if (styling.secondRowType === "allTools") {
            const tools = await fetchPublicItems<SanityToolItem>("allTools");
            secondRowData = mapToolsToMarqueeItems(tools ?? []);
          }
          setSecondRowItems(secondRowData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setItems([]);
        setSecondRowItems([]);
      } finally {
        setHasFetched(true);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    type,
    customClients,
    customTools,
    customItems,
    styling.showSecondRow,
    styling.secondRowType,
    styling.secondRowCustomClients,
    styling.secondRowCustomTools,
    styling.secondRowCustomItems,
    prefetchedItems,
    prefetchedSecondRowItems,
    shouldShowInitialLoading,
  ]);

  // Remove duplicates from items array
  const removeDuplicates = (items: MarqueeItem[]): MarqueeItem[] => {
    const seen = new Set();
    return items.filter(item => {
      const key = item._id || item.name;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  // Create staggered items for second row to prevent simultaneous duplication
  const createStaggeredSecondRow = (items: MarqueeItem[]): MarqueeItem[] => {
    if (items.length <= 1) return items;
    
    // Create a more sophisticated staggered effect
    // Reverse the order and shift by 1/3 to create maximum separation
    const reversed = [...items].reverse();
    const shiftAmount = Math.ceil(items.length / 3);
    
    // Shift the reversed array
    const shifted = [
      ...reversed.slice(shiftAmount),
      ...reversed.slice(0, shiftAmount)
    ];
    
    return shifted;
  };

  // Get logo style classes
  const getLogoStyleClasses = () => {
    const baseClasses = `object-contain opacity-${logoOpacity / 25}`;
    
    switch (logoStyle) {
      case "grayscale":
        return cn(baseClasses, "grayscale");
      case "monochrome":
        return cn(baseClasses, "grayscale brightness-0 invert");
      case "color":
      default:
        return baseClasses;
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <section className={`${backgroundColor} ${padding}`}>
        <div className="container mx-auto px-6">
          <div className="text-center py-12">
            <p className="text-text">Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  // If no items, show a message
  if (hasFetched && (!items || items.length === 0)) {
    return (
      <section className={`${backgroundColor} ${padding}`}>
        <div className="container mx-auto px-6">
          <div className="text-center py-12">
            <p className="text-text">
              {type === "allClients" && "No clients with logos found. Please add clients with logos in Sanity Studio."}
              {type === "allTools" && "No tools with icons found. Please add tools with icons in Sanity Studio."}
              {type === "custom" && "No custom items selected. Please select items in Sanity Studio."}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`${backgroundColor} ${padding}`}>
      <div className="container mx-auto px-6">
        {(heading || subheading || description) && (
          <div className={`mb-12 ${textAlignment}`}>
            {subheading && (
              <p className="text-subheading uppercase tracking-wider mb-3">
                {subheading}
              </p>
            )}
            {heading && (
              <h2 className="text-4xl font-normal text-heading mb-6">
                {heading}
              </h2>
            )}
            {description && (
              <p className="text-text max-w-2xl mx-auto text-base leading-relaxed">
                {description}
              </p>
            )}
          </div>
        )}

        <div className="relative">
          <div className="absolute inset-y-0 w-32 bg-gradient-to-r from-bg from-30% to-transparent z-10 -left-10"></div>
          <div className="overflow-hidden">
            <Marquee gradient={false} speed={speed}>
              {removeDuplicates(items).map((item, index) => (
                <div
                  key={`${item._id || item.name}-${index}`}
                  className="flex-shrink-0 mx-2 flex items-center justify-center rounded-[15px] p-4 bg-white"
                >
                  {item.logo ? (
                    <div
                      className={cn(
                        "w-[200px] h-[50px] bg-contain bg-center bg-no-repeat",
                        getLogoStyleClasses(),
                      )}
                      style={{
                        backgroundImage: `url(${urlFor(item.logo.asset).url()})`,
                      }}
                      title={item.logo.alt || item.name}
                    />
                  ) : (
                    <div className="w-[180px] h-[100px] flex items-center justify-center text-text text-sm font-medium border border-white/10 rounded-lg p-2">
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </Marquee>
          </div>
          <div className="absolute inset-y-0 w-32 bg-gradient-to-l from-bg from-30% to-transparent z-10 -right-10"></div>
        </div>

        {/* Second Row - Reverse Direction */}
        {styling.showSecondRow && secondRowItems.length > 0 && (
          <div className="relative mt-4">
            <div className="absolute inset-y-0 w-32 bg-gradient-to-r from-bg from-30% to-transparent z-10 -left-10"></div>
            <div className="overflow-hidden">
              <Marquee
                gradient={false}
                speed={styling.secondRowSpeed || speed}
                direction="right"
              >
                {createStaggeredSecondRow(removeDuplicates(secondRowItems)).map(
                  (item, index) => (
                    <div
                      key={`second-${item._id || item.name}-${index}`}
                      className="flex-shrink-0 mx-2 flex items-center justify-center rounded-[15px] p-4 bg-white"
                    >
                      {item.logo ? (
                        <div
                          className={cn(
                            "w-[200px] h-[50px] bg-contain bg-center bg-no-repeat",
                            getLogoStyleClasses(),
                          )}
                          style={{
                            backgroundImage: `url(${urlFor(item.logo.asset).url()})`,
                          }}
                          title={item.logo.alt || item.name}
                        />
                      ) : (
                        <div className="w-[180px] h-[100px] flex items-center justify-center text-text text-sm font-medium border border-white/10 rounded-lg p-2">
                          {item.name}
                        </div>
                      )}
                    </div>
                  ),
                )}
              </Marquee>
            </div>
            <div className="absolute inset-y-0 w-32 bg-gradient-to-l from-bg from-30% to-transparent z-10 -right-10"></div>
          </div>
        )}
      </div>
    </section>
  );
}
